import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { SortBy, SortOrder, SupportApiService, Status as SupportStatus } from '@vendasta/support';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  combineLatest,
  map,
  skipWhile,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { ConversationPipesModule } from '../../../../../../../pipes';
import { InboxTicketCardComponent } from '../inbox-ticket-card/inbox-ticket-card.component';
import { TicketInfo } from './ticket-info';

type Status = 'open' | 'closed';

enum statusEnum {
  OPEN = 'open',
  CLOSED = 'closed',
}

const CLOSED_STATUS = [SupportStatus.STATUS_CLOSED, SupportStatus.STATUS_SOLVED];
const OPEN_STATUS = [
  SupportStatus.STATUS_HOLD,
  SupportStatus.STATUS_NEW,
  SupportStatus.STATUS_OPEN,
  SupportStatus.STATUS_PENDING,
];
const PAGE = 5;

interface TicketList {
  tickets: TicketInfo[];
  total: number;
  continue: boolean;
}
@Component({
  selector: 'inbox-ticket-list',
  templateUrl: './inbox-ticket-list.component.html',
  styleUrls: ['./inbox-ticket-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    GalaxyPipesModule,
    MatIconModule,
    TranslateModule,
    ConversationPipesModule,
    InboxTicketCardComponent,
    GalaxyFormFieldModule,
    MatCardModule,
    GalaxyLoadingSpinnerModule,
    GalaxyAlertModule,
  ],
})
export class InboxTicketListComponent implements OnInit {
  @Input() set partner(partnerId: string) {
    this.partnerId$$.next(partnerId);
  }
  @Input() title: string;
  @Input() status: Status;

  private partnerId$$ = new ReplaySubject<string>(1);
  statusName: string;

  private readonly isLoading$$ = new BehaviorSubject<boolean>(true);
  protected readonly isLoading$ = this.isLoading$$.asObservable();

  private showMore$$ = new BehaviorSubject<number>(PAGE);
  private nextPage$ = this.showMore$$.asObservable();

  readonly tickets$ = this.partnerId$$.pipe(
    skipWhile((partner) => !partner),
    tap(() => this.isLoading$$.next(true)),
    tap(() => this.showMore$$.next(PAGE)),
    switchMap((partnerId) =>
      this.supportService.searchTickets({
        partnerId: partnerId,
        searchTerm: '',
        sortBy: this.status === statusEnum.OPEN ? SortBy.SORT_BY_CREATED_AT : SortBy.SORT_BY_UPDATED_AT,
        sortOrder: SortOrder.SORT_ORDER_DESC,
        statuses: this.status === statusEnum.OPEN ? OPEN_STATUS : CLOSED_STATUS,
      }),
    ),
    map((res) => {
      return {
        tickets: res?.tickets?.map((ticket) => new TicketInfo(ticket)) || [],
        total: res?.count || 0,
      } as TicketList;
    }),
    startWith({ tickets: [], total: 0, continue: false } as TicketList),
    tap(() => this.isLoading$$.next(false)),
  );

  pagedTickets$: Observable<TicketList> = combineLatest([this.tickets$, this.nextPage$]).pipe(
    map(([tickets, page]) => {
      return {
        tickets: tickets.tickets.slice(0, page),
        total: tickets.total,
        continue: page < tickets.total,
      } as TicketList;
    }),
  );

  constructor(private readonly supportService: SupportApiService) {}

  ngOnInit() {
    this.statusName = statusEnum.OPEN === this.status ? statusEnum.OPEN : statusEnum.CLOSED;
  }

  loadClosed(): void {
    this.showMore$$.next(this.showMore$$.value + 5);
  }
}
